import { useState, useRef } from 'react';
import { Upload, Sparkles, CheckCircle, Package, ArrowLeft, ArrowRight, Loader2, AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminAxios } from './AdminLayout';

export default function AdminBulkOffers() {
  var [step, setStep] = useState('input'); // input, review, processing, grouping, result
  var [jobId, setJobId] = useState(null);
  var [rows, setRows] = useState([]);
  var [totalRows, setTotalRows] = useState(0);
  var [rawText, setRawText] = useState('');
  var [template, setTemplate] = useState('premium_wholesale');
  var [parsing, setParsing] = useState(false);
  var [processing, setProcessing] = useState(false);
  var [processedCount, setProcessedCount] = useState(0);
  var [groups, setGroups] = useState([]);
  var [selectedGroups, setSelectedGroups] = useState([]);
  var [generating, setGenerating] = useState(false);
  var [result, setResult] = useState(null);
  var [groupMode, setGroupMode] = useState('category');
  var [lookupInput, setLookupInput] = useState('');
  var [lookupResults, setLookupResults] = useState(null);
  var [lookingUp, setLookingUp] = useState(false);
  var fileRef = useRef(null);

  var templates = [
    {id: 'premium_wholesale', name: 'Premium Wholesale'},
    {id: 'clearance', name: 'Clearance Stock'},
    {id: 'high_margin', name: 'High Margin Opportunity'},
    {id: 'limited_allocation', name: 'Limited Allocation'},
    {id: 'weekly_update', name: 'Weekly Stock Update'},
    {id: 'new_arrival', name: 'New Arrival'},
  ];

  async function handleFileUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    setParsing(true);
    try {
      var fd = new FormData();
      fd.append('file', file);
      var res = await adminAxios().post('/api/admin/bulk-offers/upload-file?template=' + template, fd);
      setJobId(res.data.job_id);
      setTotalRows(res.data.total_rows);
      await loadJob(res.data.job_id);
      setStep('review');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Upload failed'); }
    finally { setParsing(false); }
  }

  async function handleTextUpload() {
    if (!rawText.trim()) return;
    setParsing(true);
    try {
      var res = await adminAxios().post('/api/admin/bulk-offers/upload-text', { raw_text: rawText, template: template });
      setJobId(res.data.job_id);
      setTotalRows(res.data.total_rows);
      await loadJob(res.data.job_id);
      setStep('review');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Parse failed'); }
    finally { setParsing(false); }
  }

  async function loadJob(id) {
    var res = await adminAxios().get('/api/admin/bulk-offers/' + id);
    setRows(res.data.rows);
    setTotalRows(res.data.total_rows);
    setGroups(res.data.groups || []);
  }

  async function processAllBatches() {
    setStep('processing');
    setProcessing(true);
    setProcessedCount(0);
    var nextStart = 0;
    var batchSize = 20;

    while (nextStart !== null) {
      try {
        var res = await adminAxios().post('/api/admin/bulk-offers/process-batch', { job_id: jobId, batch_start: nextStart, batch_size: batchSize });
        setProcessedCount(res.data.processed_end);
        nextStart = res.data.next_batch_start;
      } catch (err) {
        alert('Batch processing error at row ' + nextStart + '. You can retry.');
        break;
      }
    }

    await loadJob(jobId);
    setProcessing(false);
    setStep('review');
  }

  async function groupProducts() {
    try {
      var res = await adminAxios().post('/api/admin/bulk-offers/group?job_id=' + jobId + '&mode=' + groupMode);
      setGroups(res.data.groups);
      setSelectedGroups(res.data.groups.map(function(_, i) { return i; }));
      setStep('grouping');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Grouping failed'); }
  }

  async function generateOffers() {
    setGenerating(true);
    try {
      var res = await adminAxios().post('/api/admin/bulk-offers/generate', { job_id: jobId, group_indices: selectedGroups, template: template });
      setResult(res.data);
      setStep('result');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Generation failed'); }
    finally { setGenerating(false); }
  }

  function toggleGroup(idx) {
    if (selectedGroups.indexOf(idx) >= 0) setSelectedGroups(selectedGroups.filter(function(i) { return i !== idx; }));
    else setSelectedGroups(selectedGroups.concat([idx]));
  }

  var dupeCount = rows.filter(function(r) { return r.duplicate_of !== null && r.duplicate_of !== undefined; }).length;
  var aiNeeded = rows.filter(function(r) { return r.needs_ai; }).length;

  async function handleLookup() {
    if (!lookupInput.trim()) return;
    setLookingUp(true);
    try {
      var ids = lookupInput.split(/[\n,;]+/).map(function(s) { return s.trim(); }).filter(Boolean);
      var res = await adminAxios().post('/api/admin/bulk-offers/lookup', { identifiers: ids });
      setLookupResults(res.data);
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Lookup failed'); }
    finally { setLookingUp(false); }
  }

  return (
    <div data-testid="admin-bulk-offers-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/offers" className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Bulk AI Offer Generator</h2>
      </div>

      {step === 'input' && (
        <div className="space-y-6">
          {/* Template */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <label className="font-manrope text-sm font-semibold text-slate-700 block mb-2">Offer Template</label>
            <div className="flex flex-wrap gap-2">
              {templates.map(function(t) {
                return <button key={t.id} type="button" onClick={function(){setTemplate(t.id);}} className={'px-4 py-2 rounded-xl font-manrope text-sm font-medium transition-colors ' + (template === t.id ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>{t.name}</button>;
              })}
            </div>
          </div>

          {/* Upload */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3"><Upload className="w-5 h-5 text-[#0096D6]" /><h3 className="font-outfit text-lg font-semibold text-slate-900">Upload Supplier File</h3></div>
            <p className="font-manrope text-sm text-slate-500 mb-3">CSV, Excel (XLSX), PDF, or TXT. Supports thousands of product rows.</p>
            <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#0096D6] text-[#0096D6] font-manrope text-sm font-semibold cursor-pointer hover:bg-[#0096D6]/5">
              <Upload className="w-4 h-4" /> {parsing ? 'Processing...' : 'Choose File'}
              <input ref={fileRef} type="file" className="hidden" accept=".csv,.xlsx,.xls,.pdf,.txt" onChange={handleFileUpload} />
            </label>
          </div>

          {/* Paste */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-[#0096D6]" /><h3 className="font-outfit text-lg font-semibold text-slate-900">Paste Product List</h3></div>
            <p className="font-manrope text-xs text-slate-500 mb-3">Paste messy supplier lists, WhatsApp stock lists, email stock lists, or spreadsheet rows. AI will identify each complete product record from the raw text.</p>
            <textarea value={rawText} onChange={function(e){setRawText(e.target.value);}} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm h-40 resize-none mb-3" placeholder={"SDSDXXXD-256G-GN4IN 619659188658 SanDisk Extreme Pro SD UHS I 256GB 200MB/s *3240 Price $57.89\nSDSDXXXD-128G-GN4IN 619659188634 SanDisk Extreme Pro SD UHS I 128GB 90MB/s *5000 Price $29.50"} />
            <button onClick={handleTextUpload} disabled={parsing || !rawText.trim()} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {parsing ? 'AI Parsing Products...' : 'Parse & Identify Products'}
            </button>
          </div>

          {/* EAN/UPC/MPN Lookup */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3"><Package className="w-5 h-5 text-[#0096D6]" /><h3 className="font-outfit text-lg font-semibold text-slate-900">Product Lookup (EAN/UPC/MPN)</h3></div>
            <p className="font-manrope text-sm text-slate-500 mb-3">Enter EAN, UPC, or part numbers. One per line or comma-separated.</p>
            <textarea value={lookupInput} onChange={function(e){setLookupInput(e.target.value);}} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm h-20 resize-none mb-3" placeholder="740617344790&#10;CT1000P3PSSD8" />
            <button onClick={handleLookup} disabled={lookingUp || !lookupInput.trim()} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50">
              {lookingUp ? 'Looking up...' : 'Lookup Products'}
            </button>
            {lookupResults && (
              <div className="mt-4 space-y-2">
                <p className="font-manrope text-sm text-slate-700">{lookupResults.identified} identified, {lookupResults.unidentified} unidentified</p>
                {lookupResults.results.map(function(r, i) {
                  return <div key={i} className={'p-3 rounded-lg border text-sm font-manrope ' + (r.identified ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50')}>
                    <strong>{r.input_identifier}</strong>: {r.identified ? r.name + ' (' + r.brand + ') — ' + r.category : 'Not identified — needs manual review'}
                  </div>;
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'review' && (
        <div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm mb-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-4">
              <span className="font-manrope text-sm"><strong>{totalRows}</strong> products</span>
              {dupeCount > 0 && <span className="font-manrope text-sm text-amber-600"><AlertTriangle className="w-3 h-3 inline" /> {dupeCount} duplicates</span>}
              {aiNeeded > 0 && <span className="font-manrope text-sm text-blue-600">{aiNeeded} need AI processing</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={processAllBatches} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500 text-white font-manrope text-sm font-semibold">
                <Sparkles className="w-4 h-4" /> AI Process All
              </button>
              <select value={groupMode} onChange={function(e){setGroupMode(e.target.value);}} className="px-3 py-2 rounded-xl border border-slate-200 font-manrope text-sm">
                <option value="category">Group by Category</option>
                <option value="brand">Group by Brand</option>
                <option value="location">Group by Location</option>
                <option value="currency">Group by Currency</option>
                <option value="incoterms">Group by Incoterms</option>
                <option value="eta">Group by ETA</option>
              </select>
              <button onClick={groupProducts} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">
                <Package className="w-4 h-4" /> Group & Generate
              </button>
            </div>
          </div>

          {/* Product table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{maxHeight: '500px', overflowY: 'auto'}}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Product</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">SKU/MPN</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">EAN</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Qty</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Price</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Category</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map(function(row, i) {
                    var isDupe = row.duplicate_of !== null && row.duplicate_of !== undefined;
                    var conf = row.confidence || 0;
                    var confColor = conf >= 90 ? 'bg-green-100 text-green-700' : conf >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                    return (
                      <tr key={i} className={'border-b border-slate-50 ' + (isDupe ? 'bg-amber-50/50' : conf < 80 ? 'bg-red-50/30' : '')}>
                        <td className="px-3 py-2 font-manrope max-w-[200px]">
                          <span className="font-medium text-slate-900 block truncate">{row.name || '-'}</span>
                          {row.brand && <span className="text-[10px] text-slate-400">{row.brand}</span>}
                        </td>
                        <td className="px-3 py-2 font-manrope text-slate-600 font-mono text-[10px]">{row.mpn || '-'}</td>
                        <td className="px-3 py-2 font-manrope text-slate-500 font-mono text-[10px]">{row.ean || '-'}</td>
                        <td className="px-3 py-2 font-manrope">{row.quantity || '-'}</td>
                        <td className="px-3 py-2 font-manrope font-medium">{row.price || '-'}</td>
                        <td className="px-3 py-2 font-manrope text-slate-500">{row.category || '-'}</td>
                        <td className="px-3 py-2">
                          <span className={'px-1.5 py-0.5 rounded-full text-[9px] font-semibold ' + confColor}>{conf}%</span>
                          {isDupe && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700">Dup</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalRows > 200 && <div className="p-3 text-center font-manrope text-xs text-slate-400">Showing first 200 of {totalRows} rows</div>}
          </div>
        </div>
      )}

      {step === 'processing' && (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 shadow-sm text-center">
          <Loader2 className="w-12 h-12 text-[#0096D6] mx-auto mb-4 animate-spin" />
          <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">AI Processing Products</h3>
          <p className="font-manrope text-slate-600 mb-4">Processing {processedCount} of {totalRows} products...</p>
          <div className="w-full max-w-md mx-auto bg-slate-100 rounded-full h-3 mb-2">
            <div className="bg-[#0096D6] rounded-full h-3 transition-all" style={{width: (totalRows > 0 ? Math.round(processedCount / totalRows * 100) : 0) + '%'}}></div>
          </div>
          <p className="font-manrope text-xs text-slate-400">{totalRows > 0 ? Math.round(processedCount / totalRows * 100) : 0}% complete — cleaning, translating, categorising</p>
          <p className="font-manrope text-[10px] text-amber-600 mt-4"><AlertTriangle className="w-3 h-3 inline" /> AI-generated descriptions must be reviewed before sending.</p>
        </div>
      )}

      {step === 'grouping' && (
        <div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-4">
            <h3 className="font-outfit text-lg font-bold text-slate-900 mb-3">Offer Groups ({groups.length})</h3>
            <p className="font-manrope text-sm text-slate-500 mb-4">Products have been grouped by category. Select which groups to generate as offer drafts.</p>
            <div className="space-y-2">
              {groups.map(function(g, i) {
                var selected = selectedGroups.indexOf(i) >= 0;
                return (
                  <div key={i} onClick={function(){toggleGroup(i);}} className={'flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-colors ' + (selected ? 'border-[#0096D6] bg-[#0096D6]/5' : 'border-slate-200 hover:border-slate-300')}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={selected} readOnly className="w-4 h-4 rounded" />
                      <div>
                        <span className="font-manrope text-sm font-semibold text-slate-900">{g.name}</span>
                        <span className="font-manrope text-xs text-slate-500 ml-2">{g.product_count} products</span>
                      </div>
                    </div>
                    {g.location && <span className="font-manrope text-xs text-slate-400">{g.location}</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={function(){setStep('review');}} className="px-4 py-2 rounded-xl border border-slate-200 font-manrope text-sm">Back to Review</button>
            <button onClick={generateOffers} disabled={generating || selectedGroups.length === 0} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50">
              <Package className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate ' + selectedGroups.length + ' Offer Drafts'}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">{result.offers_created} Offer Drafts Created</h3>
          <p className="font-manrope text-slate-600 mb-6">Review and send from the Offers page.</p>
          <div className="space-y-2 max-w-md mx-auto mb-6 text-left">
            {(result.offers || []).map(function(o) {
              return <div key={o.id} className="px-4 py-3 bg-slate-50 rounded-xl flex items-center justify-between">
                <span className="font-manrope text-sm text-slate-900">{o.subject}</span>
                <span className="font-manrope text-xs text-slate-400">{o.product_count} products</span>
              </div>;
            })}
          </div>
          <div className="flex justify-center gap-3">
            <Link to="/admin/offers" className="px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">View Offers</Link>
            <button onClick={function(){setStep('input');setJobId(null);setRows([]);setRawText('');setResult(null);}} className="px-5 py-2.5 rounded-xl border border-slate-200 font-manrope text-sm">Process More</button>
          </div>
          <p className="font-manrope text-[10px] text-amber-600 mt-6"><AlertTriangle className="w-3 h-3 inline" /> AI-generated product descriptions must be reviewed before sending. Pricing, stock, and availability must be verified before broadcast.</p>
        </div>
      )}
    </div>
  );
}
